import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { verseService } from '../../../services/supabaseService';

const VerseCompletionModal = ({ verse, isOpen, onClose, onRepeat, onContinueToNext, userId }) => {
  const [selectedPhase, setSelectedPhase] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [verseInLibrary, setVerseInLibrary] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const checkVerseInLibrary = async () => {
      if (!userId || !verse || !isOpen) return;
      try {
        const { data, error: fetchError } = await verseService?.getUserVerses(userId);
        if (!fetchError && data) {
          const existingVerse = data?.find(
            v => v?.reference === verse?.reference ||
            (v?.book === verse?.book && v?.chapter === verse?.chapter && v?.verse === verse?.verse)
          );
          if (existingVerse) {
            setVerseInLibrary(true);
            setSelectedPhase(existingVerse?.phase || 1);
          } else {
            setVerseInLibrary(false);
            setSelectedPhase(1);
          }
        }
      } catch (err) {
        console.error('Error checking verse in library:', err);
      }
    };
    checkVerseInLibrary();
  }, [userId, verse, isOpen]);

  if (!isOpen) return null;

  const handleAddToLibrary = async () => {
    if (!userId || !verse) return;
    setIsProcessing(true);
    setError('');
    setSuccessMessage('');
    try {
      const { data, error: createError } = await verseService?.createVerseFromBibleText(
        userId,
        verse?.book,
        verse?.chapter,
        verse?.verse,
        verse?.verseEnd || verse?.verse,
        verse?.text,
        verse?.reference,
        verse?.version || 'kjv'
      );
      if (createError) {
        setError(createError?.message || 'Failed to add verse to library');
        return;
      }
      if (selectedPhase !== 1 && data?.id) {
        const { error: updateError } = await verseService?.updateVersePhase(data?.id, selectedPhase, 0);
        if (updateError) console.error('Error updating phase:', updateError);
      }
      setVerseInLibrary(true);
      setSuccessMessage('Verse added to library successfully!');
      setTimeout(() => {
        if (onContinueToNext) onContinueToNext();
        else onClose();
      }, 1500);
    } catch (err) {
      console.error('Error adding verse to library:', err);
      setError(err?.message || 'Failed to add verse');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleModifyPhase = async () => {
    if (!userId || !verse) { setError('Unable to modify phase'); return; }
    setIsProcessing(true);
    setError('');
    setSuccessMessage('');
    try {
      const { data: verses, error: fetchError } = await verseService?.getUserVerses(userId);
      if (fetchError) { setError('Failed to fetch verse from library'); return; }
      const existingVerse = verses?.find(
        v => v?.reference === verse?.reference ||
        (v?.book === verse?.book && v?.chapter === verse?.chapter && v?.verse === verse?.verse)
      );
      if (!existingVerse?.id) { setError('Verse not found in library'); return; }
      const { error: updateError } = await verseService?.updateVersePhase(
        existingVerse?.id, selectedPhase, existingVerse?.reviewCount || 0
      );
      if (updateError) { setError(updateError?.message || 'Failed to update phase'); return; }
      setSuccessMessage('Phase updated successfully!');
      setTimeout(() => {
        if (onContinueToNext) onContinueToNext();
        else onClose();
      }, 1500);
    } catch (err) {
      console.error('Error modifying phase:', err);
      setError(err?.message || 'Failed to modify phase');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRepeatSession = () => {
    if (onRepeat) onRepeat();
    onClose();
  };

  const handleContinue = () => {
    if (onContinueToNext) onContinueToNext();
    else onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-md w-full p-6 animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-card-foreground">Verse Complete!</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Verse Reference */}
        <div className="mb-6 p-4 bg-success/10 border border-success/30 rounded-lg">
          <div className="flex items-center gap-2 text-success">
            <Icon name="CheckCircle" size={20} />
            <span className="font-semibold">{verse?.reference}</span>
          </div>
          <p className="text-sm text-success/80 mt-1">
            Great job! You've completed the chunking session.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg animate-shake">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-3 bg-success/10 border border-success/30 rounded-lg animate-fadeIn">
            <p className="text-sm text-success">{successMessage}</p>
          </div>
        )}

        {/* Options */}
        <div className="space-y-4 mb-6">
          {/* Phase Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {verseInLibrary ? 'Update Phase' : 'Select Phase (for library)'}
            </label>
            <select
              value={selectedPhase}
              onChange={(e) => setSelectedPhase(parseInt(e?.target?.value))}
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
              disabled={isProcessing}
            >
              <option value={0}>Phase 0 (Future)</option>
              <option value={1}>Phase 1 (New)</option>
              <option value={2}>Phase 2 (Learning)</option>
              <option value={3}>Phase 3 (Familiar)</option>
              <option value={4}>Phase 4 (Known)</option>
              <option value={5}>Phase 5 (Strong)</option>
              <option value={6}>Phase 6 (Mastered)</option>
              <option value={7}>Phase 7 (Expert)</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            {!verseInLibrary ? (
              <Button
                fullWidth
                onClick={handleAddToLibrary}
                disabled={isProcessing}
                iconName="Plus"
                iconPosition="left"
                className="transition-all hover:scale-105"
              >
                {isProcessing ? 'Adding...' : 'Add to Library'}
              </Button>
            ) : (
              <Button
                fullWidth
                onClick={handleModifyPhase}
                disabled={isProcessing}
                iconName="Edit"
                iconPosition="left"
                className="transition-all hover:scale-105"
              >
                {isProcessing ? 'Updating...' : 'Modify Phase'}
              </Button>
            )}

            <Button
              fullWidth
              variant="outline"
              onClick={handleRepeatSession}
              disabled={isProcessing}
              iconName="RotateCcw"
              iconPosition="left"
              className="transition-all hover:scale-105"
            >
              Repeat Chunking
            </Button>

            <Button
              fullWidth
              variant="ghost"
              onClick={handleContinue}
              disabled={isProcessing}
              iconName="ArrowRight"
              iconPosition="right"
              className="transition-all hover:scale-105"
            >
              {onContinueToNext ? 'Continue to Next Verse' : 'Close'}
            </Button>
          </div>
        </div>

        {/* Info Text */}
        <div className="text-xs text-muted-foreground text-center">
          <p>Phase 0 verses are for future memorization</p>
          <p>Phase 1-7 follow spaced repetition schedule</p>
        </div>
      </div>
    </div>
  );
};

export default VerseCompletionModal;